import { useQuery, keepPreviousData } from '@tanstack/react-query';

import api from '@/app/_module/services/api';
import { getApiUrl } from '@/app/_module/services/config';
import { endpoint } from '@/app/_module/services/endpoints';
import { handleApiCall } from '@/app/_module/utils/apiResponseHandler';

import type { StatusTrackingType } from '../types/leadassign-types';

interface StatusTrackingResponse {
  summary: {
    total: number;
    assigned: number;
    unassigned: number;
    assignmentRate: number;
  };
  byStatus: Record<string, number>;
  activities: any[];
  assignments: any[];
}

/**
 * Hook for the lead-assignment status endpoint.
 *
 * - Default (`{ full: false }` or no arg) hits `?lite=1` — fast, returns
 *   only `summary` + `byStatus` counts. Used by the stat cards at the top
 *   of the Lead Assignment page.
 * - `{ full: true }` hits the full endpoint so `assignments` and `activities`
 *   arrays are populated. Used by the Status Overview (AllLeadStatus) list.
 *
 * These are split so the fast-path keeps the top cards responsive while the
 * slow-path only runs when the Status Overview tab is visible.
 */
export const useStatusTracking = (options?: { full?: boolean }) => {
  const full = options?.full === true;
  const url = full
    ? getApiUrl(endpoint.leadAssignment.getStatusTracking)
    : getApiUrl(`${endpoint.leadAssignment.getStatusTracking}?lite=1`);

  return useQuery<StatusTrackingResponse>({
    queryKey: ['status-tracking', full ? 'full' : 'lite'],
    queryFn: async () => {
      const response = await handleApiCall(async () => await api.get({ url }));
      return response as StatusTrackingResponse;
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
    // Full mode is expensive (~seconds) — poll less often to avoid thrashing.
    refetchInterval: full ? 60 * 1000 : 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
};
